import type { PromptTemplate, TemplateKind } from '@/types';
import { isLocalMode } from '@/local';
import { apiFetch } from './apiBase';
import {
  localClonePromptTemplate,
  localCreateGlobalPromptTemplate,
  localCreatePromptTemplate,
  localDeletePromptTemplate,
  localListGlobalPromptTemplates,
  localListPromptTemplates,
  localUpdatePromptTemplate,
} from '@/local/localBackend';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export async function listPromptTemplates(
  episodeUuid: string,
): Promise<PromptTemplate[]> {
  if (isLocalMode()) return localListPromptTemplates(episodeUuid);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/prompt-templates`,
  );
  if (!res.ok) throw new Error(`listPromptTemplates failed: ${res.status}`);
  return res.json();
}

export async function createPromptTemplate(
  episodeUuid: string,
  body: { kind: TemplateKind; name: string; body: string },
): Promise<PromptTemplate> {
  if (isLocalMode()) return localCreatePromptTemplate(episodeUuid, body);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/prompt-templates`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(`createPromptTemplate failed: ${res.status}`);
  return res.json();
}

/**
 * Edit an existing episode-scoped template in place. Throws if the target is
 * a system row (server returns 403). UI should always clone first, but we
 * surface the error in case anything bypasses the clone-and-edit flow.
 */
export async function updatePromptTemplate(
  episodeUuid: string,
  id: string,
  patch: { name?: string; body?: string },
): Promise<PromptTemplate> {
  if (isLocalMode()) return localUpdatePromptTemplate(episodeUuid, id, patch);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/prompt-templates/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(`updatePromptTemplate failed: ${res.status}`);
  return res.json();
}

/**
 * Atomically clone a template (typically a system default) into a new
 * episode-scoped row AND mark it active for that kind. Returns the new row;
 * the active template id on the episode's style settings is mutated server-
 * side, so callers should refetch style settings after this resolves.
 */
export async function clonePromptTemplate(
  episodeUuid: string,
  sourceId: string,
  name?: string,
): Promise<PromptTemplate> {
  if (isLocalMode()) return localClonePromptTemplate(episodeUuid, sourceId, name);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/prompt-templates/${encodeURIComponent(sourceId)}/clone`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(name ? { name } : {}),
    },
  );
  if (!res.ok) throw new Error(`clonePromptTemplate failed: ${res.status}`);
  return res.json();
}

export async function deletePromptTemplate(
  episodeUuid: string,
  id: string,
): Promise<void> {
  if (isLocalMode()) return localDeletePromptTemplate(episodeUuid, id);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/prompt-templates/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new Error(`deletePromptTemplate failed: ${res.status}`);
}

// ─── Admin (global) ───

export async function listGlobalPromptTemplates(): Promise<PromptTemplate[]> {
  if (isLocalMode()) return localListGlobalPromptTemplates();
  const res = await apiFetch('/api/admin/prompt-templates');
  if (!res.ok) throw new Error(`listGlobalPromptTemplates failed: ${res.status}`);
  return res.json();
}

export async function createGlobalPromptTemplate(
  body: { kind: TemplateKind; name: string; body: string },
): Promise<PromptTemplate> {
  if (isLocalMode()) return localCreateGlobalPromptTemplate(body);
  const res = await apiFetch('/api/admin/prompt-templates', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`createGlobalPromptTemplate failed: ${res.status}`);
  return res.json();
}

export async function deleteGlobalPromptTemplate(id: string): Promise<void> {
  if (isLocalMode()) return localDeletePromptTemplate('__GLOBAL__', id);
  const res = await apiFetch(
    `/api/admin/prompt-templates/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new Error(`deleteGlobalPromptTemplate failed: ${res.status}`);
}
